<template>
    <div class="leasing p-6 shadow-lg rounded-lg relative">
        <p class="text-center text-lg font-medium">
            Vous faites partie d'une entreprise qui permet l'implémentation d'un
            plan vélo ?<br />
            Il est possible d'utiliser son coût patronal pour financer ce vélo,
            avec<br />
            <strong class="text-green">une réduction de plus de 50% !</strong>
        </p>
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-4 w-full mt-4">
            <div>
                <div class="w-full">
                    <strong>Paramètres du financement</strong>

                    <div
                        class="text-left sm:text-center lg:text-left sm:grid sm:grid-cols-2 lg:grid-cols-1 sm:bg-grey lg:bg-transparent"
                    >
                        <div>
                            <label for="contract_duration">
                                Durée du contrat
                            </label>
                            <AppSelect
                                v-model="inputLeasingPrice.contractDuration"
                                :options="[
                                    { label: '24 mois', value: 24 },
                                    { label: '36 mois', value: 36 },
                                    { label: '48 mois', value: 48 },
                                ]"
                                optionLabel="label"
                                optionValue="value"
                                class="text-black w-24"
                                id="contract_duration"
                                @change="updateLeasingPrice"
                            />
                        </div>
                    </div>
                </div>
                <div class="w-full">
                    <strong
                        >Calcul impact sur salaire (à titre indicatif)</strong
                    >
                    <div>
                        <label>Mon salaire brut</label>
                        <div class="input-group">
                            <input
                                type="number"
                                v-model="input.salary"
                                class="form-control mb-0"
                                @change="updateImpactOnNetSalary"
                            />
                            <span class="input-group-text">€/mois</span>
                        </div>
                    </div>
                    <p class="mt-4 flex justify-between">
                        Montant du leasing initial :
                        <span class="text-right"
                            >{{
                                formatPrice(leasing_price?.tvac?.leasingPrice)
                            }}/mois</span
                        >
                    </p>
                    <p class="mt-4 flex justify-between">
                        <span class="w-3/4">
                            Economie cotisations patronales </span
                        ><span class="text-red text-right"
                            >-{{
                                formatPrice(
                                    impactOnNetSalary?.leasingPrice -
                                        impactOnNetSalary?.impactOnGrossSalary,
                                )
                            }}/mois</span
                        >
                    </p>
                    <p class="mt-4 flex justify-between">
                        <span class="w-3/4"> Economie imposition </span>
                        <span class="text-red text-right"
                            >-{{
                                formatPrice(
                                    impactOnNetSalary?.impactOnGrossSalary -
                                        impactOnNetSalary?.impactOnNetSalary,
                                )
                            }}/mois</span
                        >
                    </p>
                    <p class="mt-4 flex justify-between">
                        <span class="w-3/4">
                            Impact réel sur salaire net :
                        </span>
                        <strong class="text-green text-right">
                            {{
                                formatPrice(
                                    impactOnNetSalary?.impactOnNetSalary,
                                )
                            }}/mois</strong
                        >
                    </p>
                    <p class="mt-4 flex justify-between">
                        <span class="w-3/4">
                            Coût total du vélo sur
                            {{ inputLeasingPrice.contractDuration }} mois, tous
                            services compris
                        </span>
                        <strong>
                            {{
                                formatPrice(
                                    impactOnNetSalary?.impactOnNetSalary *
                                        inputLeasingPrice.contractDuration +
                                        bike?.leasing_price?.tvac
                                            ?.residualValueAmount,
                                )
                            }}</strong
                        >
                    </p>
                </div>
            </div>
            <!-- Left Block: Text Content -->
            <DoughnutLeasing
                :leasing_price="leasing_price"
                :contract_type="contract_type"
                v-if="leasing_price"
            />
        </div>

        <div class="w-full text-center">
            <primary-button
                @click="openLeasingModal = true"
                class="mt-4 font-bold text-white"
                color="green"
                >Demandez un tel avantage !</primary-button
            >
        </div>
    </div>
    <modal :open="openLeasingModal" @close="openLeasingModal = false">
        <template #title> Formulaire de demande de plan vélo </template>
        <template #content>
            <LeasingRequestForm />
        </template>
    </modal>
</template>
<!-- Attention-Grabbing Pin -->

<script setup>
// Chart options
import { useApiStore } from '@/js/stores/apiStore';
import DoughnutLeasing from '@/js/views/public/pro/catalog/partials/DoughnutLeasing.vue';
import round from '@/js/functions/round';
import { defineProps, ref } from 'vue';
import formatPrice from '@/js/functions/formatPrice';
import LeasingRequestForm from '@/js/views/public/pro/catalog/partials/LeasingRequestForm.vue';

const apiStore = useApiStore();
const openLeasingModal = ref(false);
const inputLeasingPrice = ref({
    maintenanceBudget: 100,
    contractDuration: 36,
    rcInsurance: false,
    accompte: 0,
});

const props = defineProps({
    leasing_price: Object,
    bike: Object,
    contract_type: String,
});

const bike = ref(props.bike);
const input = ref({
    salary: 3400,
});

const impactOnNetSalary = ref({});

const updateLeasingPrice = () => {
    apiStore
        .apiGet('/api/leasing-price/calculate', {
            contract_type: 'leasing',
            price_htva: round(
                (bike.value.price_htva * (100 - bike.value.promotion)) / 100,
            ),
            maintenanceBudget: inputLeasingPrice.value.maintenanceBudget,
            contract_duration: inputLeasingPrice.value.contractDuration,
            rcInsurance: inputLeasingPrice.value.rcInsurance,
            accompte: inputLeasingPrice.value.accompte,
            residual_value_check: true,
        })
        .then(response => {
            bike.value.leasing_price.tvac = response.leasing_price.tvac;
            updateImpactOnNetSalary();
        });
};

const updateImpactOnNetSalary = () => {
    apiStore
        .apiGet('/api/salary-impact/calculate', {
            salary: input.value.salary,
            homeWorkDistance: 10,
            numberOfTrips: 2,
            typeOfWorker: 'employee',
            selectedOption: 'salaire',
            leasingPrice: bike.value.leasing_price.tvac.leasingPrice,
            cotisationPatronales: 25,
            pfa: 1,
        })
        .then(response => {
            impactOnNetSalary.value = response;
        })
        .catch(error => {
            console.log(error);
        });
};

updateImpactOnNetSalary();
</script>
